import { useState, useEffect, useRef, useMemo } from "react";
import { ConfirmationResult } from "firebase/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { PhoneNumberInput } from "@/components/ui/phone-input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Shield,
  Users,
  Building2,
  CreditCard,
  Search,
  RefreshCw,
  Trash2,
  MessageSquare,
  Calendar,
  Phone,
  Mail,
  User,
  TrendingUp,
  Tag,
  Eye,
  Copy,
  Loader2,
  AlertTriangle,
  LogOut,
  FileSpreadsheet,
  Bell,
  Settings,
  Filter,
  List,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, AdminDashboardStats, AdminAccountDto, AdminUserDto, AdminTransactionDto, AdminCategoryDto } from "@/lib/api";
import { format } from "date-fns";
import { useUser } from "@/contexts/UserContext";
import { cn } from "@/lib/utils";

// Get allowed admin phone numbers from env (comma-separated)
const ALLOWED_ADMIN_PHONES = (import.meta.env.VITE_ADMIN_PHONES || "").split(",").map((p: string) => p.trim()).filter(Boolean);

// Type for account details with users, transactions, and categories
interface AccountDetails {
  account: AdminAccountDto;
  users: AdminUserDto[];
  transactions: AdminTransactionDto[];
  categories: AdminCategoryDto[];
}

export default function Admin() {
  const { sendOTP, verifyOTP } = useUser();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginPhone, setLoginPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [accounts, setAccounts] = useState<AdminAccountDto[]>([]);
  const [users, setUsers] = useState<AdminUserDto[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("accounts");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<string | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteFirebaseUsers, setDeleteFirebaseUsers] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Account details dialog
  const [accountDetailsOpen, setAccountDetailsOpen] = useState(false);
  const [selectedAccountDetails, setSelectedAccountDetails] = useState<AccountDetails | null>(null);
  const [isLoadingAccountDetails, setIsLoadingAccountDetails] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Account filter for users tab
  const [accountFilter, setAccountFilter] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const adminSession = sessionStorage.getItem("adminAuthenticated");
    if (adminSession === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Check if phone number is allowed
  const isPhoneAllowed = (phone: string): boolean => {
    // Normalize phone number (remove spaces, dashes, etc.)
    const normalizedPhone = phone.replace(/[\s\-()]/g, "");
    
    return ALLOWED_ADMIN_PHONES.some((allowed: string) => {
      const normalizedAllowed = allowed.replace(/[\s\-()]/g, "");
      return normalizedPhone.includes(normalizedAllowed) || normalizedAllowed.includes(normalizedPhone);
    });
  };

  // Handle sending OTP
  const handleSendOTP = async () => {
    setLoginError("");
    
    if (!loginPhone.trim()) {
      setLoginError("Phone number is required");
      return;
    }

    // Check if phone is in allowed list BEFORE sending OTP
    if (!isPhoneAllowed(loginPhone)) {
      setLoginError("Phone number not authorized for admin access");
      return;
    }

    try {
      setIsLoggingIn(true);
      const confirmation = await sendOTP(loginPhone);
      setConfirmationResult(confirmation);
      setStep("otp");
      toast.success("OTP sent to your phone");
    } catch (error) {
      console.error("Error sending OTP:", error);
      setLoginError(error instanceof Error ? error.message : "Failed to send OTP");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (otpValue?: string) => {
    if (!confirmationResult) return;
    
    const otpToVerify = otpValue || otp;
    setLoginError("");
    
    if (!otpToVerify.trim() || otpToVerify.length !== 6) {
      setLoginError("Please enter a valid 6-digit code");
      return;
    }

    try {
      setIsLoggingIn(true);
      await verifyOTP(confirmationResult, otpToVerify);
      
      // Success - set authenticated
      setIsAuthenticated(true);
      sessionStorage.setItem("adminAuthenticated", "true");
      sessionStorage.setItem("adminPhone", loginPhone);
      toast.success("Admin login successful");
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setLoginError("Invalid verification code");
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Handle OTP change with auto-verify
  const handleOTPChange = (value: string) => {
    setOtp(value);
    setLoginError("");
    
    // Auto-verify when 6 digits are entered
    if (value.length === 6 && !isLoggingIn) {
      handleVerifyOTP(value);
    }
  };

  // Handle resend OTP
  const handleResendOTP = () => {
    setOtp("");
    setStep("phone");
    setConfirmationResult(null);
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("adminAuthenticated");
    sessionStorage.removeItem("adminPhone");
    setLoginPhone("");
    setOtp("");
    setStep("phone");
    setConfirmationResult(null);
    toast.success("Logged out successfully");
  };

  // Send welcome email
  const handleSendWelcomeEmail = async (accountId: string) => {
    try {
      setIsSendingEmail(true);
      const result = await adminApi.sendWelcomeEmail(accountId);
      toast.success(`Welcome email sent successfully to ${result.email}`);
      
      // Refresh accounts to show updated email status
      await fetchAccounts();
    } catch (error) {
      console.error("Failed to send welcome email:", error);
      toast.error(error instanceof Error ? error.message : "Failed to send welcome email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Send activity summary email
  const handleSendActivitySummaryEmail = async (accountId: string) => {
    try {
      setIsSendingEmail(true);
      const result = await adminApi.sendActivitySummaryEmail(accountId);
      toast.success(`Activity summary email sent successfully to ${result.email}`);
    } catch (error) {
      console.error("Failed to send activity summary email:", error);
      toast.error(error instanceof Error ? error.message : "Failed to send activity summary email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Send engagement email
  const handleSendEngagementEmail = async (accountId: string) => {
    try {
      setIsSendingEmail(true);
      const result = await adminApi.sendEngagementEmail(accountId);
      toast.success(`Engagement email sent successfully to ${result.email}`);
      
      // Refresh accounts to show updated email status
      await fetchAccounts();
    } catch (error) {
      console.error("Failed to send engagement email:", error);
      toast.error(error instanceof Error ? error.message : "Failed to send engagement email");
    } finally {
      setIsSendingEmail(false);
    }
  };
  
  // Account filter for accounts tab (when clicking from users)
  const [accountsTabFilter, setAccountsTabFilter] = useState<string | null>(null);
  const [accountStatusFilter, setAccountStatusFilter] = useState<"all" | "active" | "unused" | "pending">("all");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Helper function to check if account is unused
  const isAccountUnused = (account: AdminAccountDto): boolean => {
    // Check if no categories
    const hasNoCategories = (account.categoriesCount ?? 0) === 0;
    
    // Check if no transactions in last 3 weeks
    const threeWeeksAgo = new Date();
    threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
    
    const lastTransactionDate = account.lastTransactionDate 
      ? new Date(account.lastTransactionDate) 
      : null;
    
    const noRecentTransactions = !lastTransactionDate || lastTransactionDate < threeWeeksAgo;
    
    return hasNoCategories || noRecentTransactions;
  };

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      setIsLoadingStats(true);
      const data = await adminApi.getDashboardStats();
      console.log("Admin stats response:", data);
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch admin stats:", error);
      toast.error("Failed to load dashboard stats");
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Fetch all accounts
  const fetchAccounts = async () => {
    try {
      setIsLoadingAccounts(true);
      const response = await adminApi.getAllAccounts();
      console.log("Admin accounts response:", response);
      
      // Handle different response formats
      let accountsData: AdminAccountDto[] = [];
      if (Array.isArray(response)) {
        accountsData = response;
      } else if (response && typeof response === 'object') {
        // Try common nested formats
        const data = (response as Record<string, unknown>).data || 
                     (response as Record<string, unknown>).accounts ||
                     (response as Record<string, unknown>).items ||
                     response;
        accountsData = Array.isArray(data) ? data as AdminAccountDto[] : [];
      }
      
      console.log("Parsed accounts:", accountsData);
      setAccounts(accountsData);
    } catch (error) {
      console.error("Failed to fetch accounts:", error);
      toast.error("Failed to load accounts");
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const response = await adminApi.getAllUsers();
      console.log("Admin users response:", response);
      
      // Handle different response formats
      let usersData: AdminUserDto[] = [];
      if (Array.isArray(response)) {
        usersData = response;
      } else if (response && typeof response === 'object') {
        // Try common nested formats
        const data = (response as Record<string, unknown>).data || 
                     (response as Record<string, unknown>).users ||
                     (response as Record<string, unknown>).items ||
                     response;
        usersData = Array.isArray(data) ? data as AdminUserDto[] : [];
      }
      
      console.log("Parsed users:", usersData);
      setUsers(usersData);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Open account details dialog
  const handleViewAccountDetails = async (account: AdminAccountDto) => {
    setAccountDetailsOpen(true);
    setIsLoadingAccountDetails(true);
    
    try {
      // First, filter users from the already loaded users array
      const usersFromState = users.filter(u => u.accountId === account.accountId);
      
      // If we have users in state, use them; otherwise try the API
      let usersData: AdminUserDto[] = usersFromState;
      let transactionsData: AdminTransactionDto[] = [];
      let categoriesData: AdminCategoryDto[] = [];
      
      // Fetch users if not in state
      if (usersFromState.length === 0) {
        try {
          const accountUsers = await adminApi.getUsersByAccount(account.accountId);
          console.log("Account users API response:", accountUsers);
          
          // Handle different response formats
          if (Array.isArray(accountUsers)) {
            usersData = accountUsers;
          } else if (accountUsers && typeof accountUsers === 'object') {
            const data = (accountUsers as Record<string, unknown>).data || 
                         (accountUsers as Record<string, unknown>).users ||
                         accountUsers;
            usersData = Array.isArray(data) ? data as AdminUserDto[] : [];
          }
        } catch (apiError) {
          console.error("API call failed, using state users:", apiError);
        }
      }
      
      // Fetch last 5 transactions
      try {
        const transactionsResponse = await adminApi.getAccountTransactions(account.accountId, 5);
        console.log("Account transactions API response:", transactionsResponse);
        
        if (transactionsResponse && transactionsResponse.transactions) {
          transactionsData = transactionsResponse.transactions;
        } else if (Array.isArray(transactionsResponse)) {
          transactionsData = transactionsResponse as AdminTransactionDto[];
        }
      } catch (txError) {
        console.error("Failed to fetch transactions:", txError);
      }
      
      // Fetch categories
      try {
        const categoriesResponse = await adminApi.getAccountCategories(account.accountId);
        console.log("Account categories API response:", categoriesResponse);
        
        if (categoriesResponse && categoriesResponse.categories) {
          categoriesData = categoriesResponse.categories;
        } else if (Array.isArray(categoriesResponse)) {
          categoriesData = categoriesResponse as AdminCategoryDto[];
        }
      } catch (catError) {
        console.error("Failed to fetch categories:", catError);
      }
      
      setSelectedAccountDetails({
        account,
        users: usersData,
        transactions: transactionsData,
        categories: categoriesData,
      });
    } catch (error) {
      console.error("Failed to fetch account details:", error);
      setSelectedAccountDetails({
        account,
        users: users.filter(u => u.accountId === account.accountId),
        transactions: [],
        categories: [],
      });
    } finally {
      setIsLoadingAccountDetails(false);
    }
  };

  // Navigate to users tab with account filter
  const handleViewAccountUsers = (accountId: string) => {
    setAccountFilter(accountId);
    setActiveTab("users");
  };

  // Clear account filter
  const clearAccountFilter = () => {
    setAccountFilter(null);
  };

  // Navigate to accounts tab with account filter
  const handleViewAccount = (accountId: string) => {
    setAccountsTabFilter(accountId);
    setActiveTab("accounts");
  };

  // Clear accounts tab filter
  const clearAccountsTabFilter = () => {
    setAccountsTabFilter(null);
  };

  // Update account settings
  const handleUpdateAccount = async (
    accountId: string,
    field: "whatsappEnabled" | "transactionNotificationsEnabled",
    value: boolean
  ) => {
    try {
      await adminApi.updateAccount(accountId, { [field]: value });
      setAccounts((prev) =>
        prev.map((acc) =>
          acc.accountId === accountId ? { ...acc, [field]: value } : acc
        )
      );
      toast.success("Account updated successfully");
    } catch (error) {
      console.error("Failed to update account:", error);
      toast.error("Failed to update account");
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;
    if (deleteConfirmText !== accountToDelete) {
      toast.error("Account ID does not match");
      return;
    }
    
    try {
      setIsDeleting(true);
      await adminApi.deleteAccount(accountToDelete, deleteFirebaseUsers);
      setAccounts((prev) => prev.filter((acc) => acc.accountId !== accountToDelete));
      setUsers((prev) => prev.filter((u) => u.accountId !== accountToDelete));
      toast.success(
        deleteFirebaseUsers 
          ? "Account and Firebase users deleted successfully" 
          : "Account deleted successfully"
      );
    } catch (error) {
      console.error("Failed to delete account:", error);
      toast.error("Failed to delete account");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
      setDeleteConfirmText("");
      setDeleteFirebaseUsers(false);
    }
  };

  const openDeleteDialog = (accountId: string) => {
    setAccountToDelete(accountId);
    setDeleteConfirmText("");
    setDeleteFirebaseUsers(false);
    setDeleteDialogOpen(true);
  };

  // Filter accounts by search query and accounts tab filter
  const filteredAccounts = (accounts || []).filter((account) => {
    const searchLower = searchQuery.toLowerCase();
    
    // Check if account ID matches
    const matchesAccountId = account.accountId?.toLowerCase().includes(searchLower);
    
    // Check if any user in this account matches the search (email, phone, name)
    const accountUsers = users.filter(u => u.accountId === account.accountId);
    const matchesUser = accountUsers.some(user => 
      user.email?.toLowerCase().includes(searchLower) ||
      user.phoneNumber?.toLowerCase().includes(searchLower) ||
      user.fullName?.toLowerCase().includes(searchLower)
    );
    
    const matchesSearch = matchesAccountId || matchesUser;
    const matchesFilter = !accountsTabFilter || account.accountId === accountsTabFilter;
    const matchesStatus =
      accountStatusFilter === "all" ||
      (accountStatusFilter === "active" && !isAccountUnused(account)) ||
      (accountStatusFilter === "unused" && isAccountUnused(account)) ||
      (accountStatusFilter === "pending" && !account.onboardingCompleted);

    return matchesSearch && matchesFilter && matchesStatus;
  });

  const activeAccountsCount = useMemo(
    () => accounts.filter((a) => !isAccountUnused(a)).length,
    [accounts],
  );
  const unusedAccountsCount = useMemo(
    () => accounts.filter((a) => isAccountUnused(a)).length,
    [accounts],
  );
  const pendingOnboardingCount = useMemo(
    () => accounts.filter((a) => !a.onboardingCompleted).length,
    [accounts],
  );

  // Filter users by search query and account filter
  const filteredUsers = (users || []).filter((user) => {
    const matchesSearch = 
      user.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.fullName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAccountFilter = !accountFilter || user.accountId === accountFilter;
    
    return matchesSearch && matchesAccountFilter;
  });

  // Load data on mount
  useEffect(() => {
    fetchStats();
    fetchAccounts();
    fetchUsers();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Admin Panel</CardTitle>
            <CardDescription>
              {step === "phone" 
                ? "Enter your authorized phone number to receive a verification code"
                : "Enter the verification code sent to your phone"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loginError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{loginError}</AlertDescription>
              </Alert>
            )}

            {step === "phone" ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <PhoneNumberInput
                    value={loginPhone}
                    onChange={(value) => setLoginPhone(value || "")}
                    placeholder="Enter your phone number"
                    error={!!loginError}
                  />
                  <p className="text-xs text-muted-foreground">
                    Only authorized admin phone numbers can access this panel
                  </p>
                </div>
                
                <Button 
                  onClick={handleSendOTP} 
                  disabled={isLoggingIn || !loginPhone.trim()}
                  className="w-full"
                >
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending code...
                    </>
                  ) : (
                    <>
                      <Phone className="h-4 w-4 mr-2" />
                      Send Verification Code
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    Code sent to {loginPhone}
                  </p>
                  
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={handleOTPChange}
                      disabled={isLoggingIn}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                
                {isLoggingIn ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="text-sm text-muted-foreground">Verifying...</span>
                  </div>
                ) : (
                  <Button 
                    onClick={() => handleVerifyOTP()} 
                    disabled={otp.length !== 6}
                    className="w-full"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Verify & Login
                  </Button>
                )}
                
                <p className="text-xs text-center text-muted-foreground">
                  Code will be verified automatically when 6 digits are entered
                </p>
                
                <Button 
                  variant="outline" 
                  onClick={handleResendOTP}
                  className="w-full"
                  disabled={isLoggingIn}
                >
                  Use different phone number
                </Button>
              </div>
            )}
            
            {/* reCAPTCHA container */}
            <div id="recaptcha-container"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-[1600px] px-4 py-6 md:px-6">
        {/* Top bar: search + actions */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6">
          <div className="order-2 flex flex-1 justify-center lg:order-1">
            <div className="relative w-full max-w-2xl">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search by phone number, email, or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 rounded-xl border-border/80 bg-muted/30 pl-10 pr-20 shadow-inner"
              />
              <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 select-none rounded-md border border-border/60 bg-muted/50 px-2 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-block">
                ⌘K
              </kbd>
            </div>
          </div>
          <div className="order-1 flex items-center justify-end gap-2 lg:order-2">
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 rounded-lg" aria-label="Notifications">
              <Bell className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 rounded-lg" aria-label="Settings">
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-lg"
              onClick={() => {
                fetchStats();
                fetchAccounts();
                fetchUsers();
              }}
              aria-label="Refresh data"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-10 gap-1.5 text-muted-foreground" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4">
          {isLoadingStats ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-border/70 bg-card/60 p-4">
                <Skeleton className="mb-2 h-3 w-24" />
                <Skeleton className="h-8 w-12" />
              </div>
            ))
          ) : stats ? (
            <>
              <div className="rounded-2xl border border-border/70 bg-card/60 p-4 shadow-sm">
                <p className="text-xs font-medium text-muted-foreground">Total accounts</p>
                <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">{stats.totalAccounts}</p>
                <Badge
                  variant="secondary"
                  className="mt-2 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0 text-[10px] font-medium text-emerald-600 dark:text-emerald-400"
                >
                  +{stats.newAccountsLast30Days} last 30 days
                </Badge>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/60 p-4 shadow-sm">
                <p className="text-xs font-medium text-muted-foreground">Active</p>
                <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">{activeAccountsCount}</p>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-emerald-500/90"
                    style={{
                      width: `${stats.totalAccounts ? (activeAccountsCount / stats.totalAccounts) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/60 p-4 shadow-sm">
                <p className="text-xs font-medium text-muted-foreground">Unused</p>
                <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">{unusedAccountsCount}</p>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-amber-500/90"
                    style={{
                      width: `${stats.totalAccounts ? (unusedAccountsCount / stats.totalAccounts) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/60 p-4 shadow-sm">
                <p className="text-xs font-medium text-muted-foreground">Users</p>
                <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">{stats.totalUsers}</p>
                <p className="mt-2 text-xs text-muted-foreground">Across all accounts</p>
              </div>
              <div className="rounded-2xl border border-border/70 bg-card/60 p-4 shadow-sm">
                <p className="text-xs font-medium text-muted-foreground">Transactions</p>
                <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">{stats.totalTransactions}</p>
                <p className="mt-2 text-xs text-muted-foreground">Lifetime</p>
              </div>
            </>
          ) : null}
        </div>

        <div className="mb-2 flex items-center gap-2">
          <Shield className="h-5 w-5 text-red-500" />
          <h1 className="text-lg font-semibold tracking-tight md:text-xl">Admin</h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <TabsList className="h-auto w-fit gap-1 rounded-xl border border-border/70 bg-muted/40 p-1">
              <TabsTrigger
                value="accounts"
                className="rounded-lg px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Accounts ({accounts.length})
                  {accountsTabFilter ? (
                    <Badge variant="secondary" className="text-[10px]">
                      Filtered
                    </Badge>
                  ) : null}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="users"
                className="rounded-lg px-4 py-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Users ({users.length})
                  {accountFilter ? (
                    <Badge variant="secondary" className="text-[10px]">
                      Filtered
                    </Badge>
                  ) : null}
                </span>
              </TabsTrigger>
            </TabsList>

            {activeTab === "accounts" && (
              <div className="flex flex-wrap items-center gap-2">
                {(["all", "active", "unused", "pending"] as const).map((key) => (
                  <Button
                    key={key}
                    type="button"
                    variant={accountStatusFilter === key ? "default" : "outline"}
                    size="sm"
                    className={cn(
                      "h-8 rounded-lg px-3 text-xs capitalize",
                      accountStatusFilter === key && "shadow-sm",
                    )}
                    onClick={() => setAccountStatusFilter(key)}
                  >
                    {key === "all"
                      ? "All"
                      : key === "active"
                        ? `Active (${activeAccountsCount})`
                        : key === "unused"
                          ? `Unused (${unusedAccountsCount})`
                          : `Pending (${pendingOnboardingCount})`}
                  </Button>
                ))}
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1 rounded-lg text-xs" disabled>
                  <Filter className="h-3.5 w-3.5" />
                  More filters
                </Button>
              </div>
            )}
          </div>

        {/* Accounts Tab */}
        <TabsContent value="accounts" className="mt-0">
          <div className="overflow-hidden rounded-2xl border border-border/80 bg-card/40 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 bg-muted/20 px-4 py-3 md:px-5">
              <div>
                <h2 className="text-base font-semibold md:text-lg">All accounts</h2>
                <p className="text-xs text-muted-foreground">Manage accounts, onboarding, and integrations</p>
              </div>
              {accountsTabFilter && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="gap-1">
                    <Building2 className="h-3 w-3" />
                    Account: {accountsTabFilter.slice(0, 8)}...
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={clearAccountsTabFilter}>
                    Clear filter
                  </Button>
                </div>
              )}
            </div>
            <div className="p-0 md:p-0">
              {isLoadingAccounts ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="[&_td]:py-1.5 [&_th]:py-2 [&_th]:text-[10px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.08em] [&_th]:text-center [&_td]:text-xs">
                    <TableHeader>
                      <TableRow className="border-border/60 hover:bg-transparent">
                        <TableHead className="text-left">Account</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Activity</TableHead>
                        <TableHead>Onboarding</TableHead>
                        <TableHead>
                          <div className="inline-flex items-center justify-center gap-1">
                            <span>Emails</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 cursor-help text-muted-foreground/80" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">W = Welcome, T = Tips, E = Engagement</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                        <TableHead>Integrations</TableHead>
                        <TableHead>Last TXN</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAccounts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            No accounts found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredAccounts.map((account) => {
                          const isUnused = isAccountUnused(account);
                          const accountUsers = users.filter((u) => u.accountId === account.accountId);
                          const primaryUser = accountUsers[0];
                          const displayName =
                            primaryUser?.fullName?.trim() ||
                            primaryUser?.phoneNumber ||
                            account.phoneNumber ||
                            "Account";
                          const initial = (displayName.trim().charAt(0) || "?").toUpperCase();
                          return (
                          <TableRow
                            key={account.accountId}
                            className={cn(
                              "border-border/40 transition-colors",
                              isUnused ? "bg-amber-500/[0.06]" : "hover:bg-muted/30",
                            )}
                          >
                            <TableCell>
                              <div className="flex min-w-0 max-w-[220px] items-center gap-2.5">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-[10px] font-bold text-primary">
                                  {initial}
                                </div>
                                <div
                                  className="min-w-0 cursor-pointer"
                                  onClick={() => handleViewAccountDetails(account)}
                                  role="button"
                                  aria-label="View account details"
                                >
                                  <p className="truncate text-xs font-semibold hover:text-primary">{displayName}</p>
                                  <div className="flex items-center gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <p className="cursor-help truncate font-mono text-[10px] text-muted-foreground/90">
                                          {account.accountId.slice(0, 14)}…
                                        </p>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-xs font-mono text-xs break-all">{account.accountId}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-4 w-4 p-0 text-muted-foreground hover:text-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewAccountDetails(account);
                                      }}
                                      aria-label="Open account data"
                                    >
                                      <Eye className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                              {isUnused ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="cursor-help gap-1 border-amber-500/40 bg-amber-500/10 px-2 py-0 text-[11px] text-amber-700 dark:text-amber-300">
                                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                                      Unused
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs max-w-[250px]">
                                      <p className="font-semibold mb-1">Account is unused:</p>
                                      {(account.categoriesCount ?? 0) === 0 && (
                                        <p>• No categories created</p>
                                      )}
                                      {(() => {
                                        const threeWeeksAgo = new Date();
                                        threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 21);
                                        const lastTransactionDate = account.lastTransactionDate 
                                          ? new Date(account.lastTransactionDate) 
                                          : null;
                                        const noRecentTransactions = !lastTransactionDate || lastTransactionDate < threeWeeksAgo;
                                        return noRecentTransactions && (
                                          <p>• No transactions in last 3 weeks</p>
                                        );
                                      })()}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <Badge variant="outline" className="gap-1 border-emerald-500/40 bg-emerald-500/10 px-2 py-0 text-[11px] text-emerald-700 dark:text-emerald-300">
                                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                                  Active
                                </Badge>
                              )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {(() => {
                                const userCount = accountUsers.length || account.userCount || account.usersCount || 0;
                                const txCount = account.transactionCount ?? account.transactionsCount ?? 0;
                                const categoryCount = account.categoriesCount ?? 0;

                                return (
                                  <div className="mx-auto inline-flex items-center gap-3 px-1.5 py-0.5">
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
                                      onClick={() => handleViewAccountUsers(account.accountId)}
                                      aria-label="View account users"
                                    >
                                      <Users className="h-3.5 w-3.5" />
                                      <span className="text-xs font-semibold">{userCount}</span>
                                    </button>
                                    <span className="h-3.5 w-px bg-border/70" />
                                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                                      <CreditCard className="h-3.5 w-3.5" />
                                      <span className="text-xs font-semibold">{txCount}</span>
                                    </span>
                                    <span className="h-3.5 w-px bg-border/70" />
                                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                                      <Tag className="h-3.5 w-3.5" />
                                      <span className="text-xs font-semibold">{categoryCount}</span>
                                    </span>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                              {account.onboardingCompleted ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  ✓ Done
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                  Pending
                                </Badge>
                              )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "h-5 min-w-5 rounded-md px-1 text-[10px] font-semibold cursor-help",
                                        account.welcomeEmailSentAt
                                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                                          : "border-border/70 bg-muted/20 text-muted-foreground",
                                      )}
                                    >
                                      W
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <p className="font-semibold">Welcome email</p>
                                      <p>
                                      {account.welcomeEmailSentAt 
                                        ? `Sent: ${format(new Date(account.welcomeEmailSentAt), "PPp")}`
                                        : "Not sent"}
                                      </p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "h-5 min-w-5 rounded-md px-1 text-[10px] font-semibold cursor-help",
                                        account.activitySummaryEmailSentAt
                                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                                          : "border-border/70 bg-muted/20 text-muted-foreground",
                                      )}
                                    >
                                      T
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <p className="font-semibold">Tips email</p>
                                      <p>
                                      {account.activitySummaryEmailSentAt 
                                        ? `Sent: ${format(new Date(account.activitySummaryEmailSentAt), "PPp")}`
                                        : "Not sent"}
                                      </p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "h-5 min-w-5 rounded-md px-1 text-[10px] font-semibold cursor-help",
                                        account.engagementEmailSentAt
                                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                                          : "border-border/70 bg-muted/20 text-muted-foreground",
                                      )}
                                    >
                                      E
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <p className="font-semibold">Engagement email</p>
                                      <p>
                                      {account.engagementEmailSentAt 
                                        ? `Sent: ${format(new Date(account.engagementEmailSentAt), "PPp")}`
                                        : "Not sent"}
                                      </p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="mx-auto inline-flex items-center justify-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "h-6 w-6 rounded-md border p-0 cursor-help",
                                        account.whatsappEnabled
                                          ? "border-emerald-500/45 bg-emerald-500/18 text-emerald-300"
                                          : "border-border/70 bg-muted/20 text-muted-foreground",
                                      )}
                                    >
                                      <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">WhatsApp {account.whatsappEnabled ? "on" : "off"}</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "h-6 w-6 rounded-md border p-0 cursor-help",
                                        account.transactionNotificationsEnabled
                                          ? "border-indigo-500/45 bg-indigo-500/18 text-indigo-300"
                                          : "border-border/70 bg-muted/20 text-muted-foreground",
                                      )}
                                    >
                                      <List className="h-3.5 w-3.5" aria-hidden />
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">
                                      Notifications {account.transactionNotificationsEnabled ? "on" : "off"}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                            <TableCell className="text-center text-xs">
                              {account.lastTransactionDate ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className="font-semibold text-foreground">
                                    {format(new Date(account.lastTransactionDate), "dd/MM/yyyy HH:mm")}
                                  </span>
                                  <span className="text-[10px] text-muted-foreground">
                                    created {format(new Date(account.createdAt), "dd/MM/yyyy")}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 px-4 py-3 text-xs text-muted-foreground md:px-5">
              <span>
                Showing {filteredAccounts.length} of {accounts.length}
              </span>
              <div className="flex items-center gap-1">
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled aria-label="Previous page">
                  ←
                </Button>
                <Button type="button" size="sm" variant="secondary" className="h-8 min-w-8 px-2 text-xs" disabled>
                  1
                </Button>
                <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled aria-label="Next page">
                  →
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>All Users</CardTitle>
                  <CardDescription>
                    View all users in the system with their Firebase data
                  </CardDescription>
                </div>
                {accountFilter && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="gap-1">
                      <Building2 className="h-3 w-3" />
                      Account: {accountFilter.slice(0, 8)}...
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={clearAccountFilter}>
                      Clear Filter
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingUsers ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Account ID</TableHead>
                        <TableHead>Terms</TableHead>
                        <TableHead>Last Sign-In</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            No users found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((user) => (
                          <TableRow key={user.userId}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                  <User className="h-4 w-4 text-primary" />
                                </div>
                                <span className="font-medium">{user.fullName}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                {user.email}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                {user.phoneNumber || "-"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span 
                                      className="font-mono text-xs cursor-pointer text-primary hover:underline"
                                      onClick={() => handleViewAccount(user.accountId)}
                                    >
                                      {user.accountId.slice(0, 8)}...
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="font-mono text-xs">{user.accountId}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Click to view account</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => {
                                    navigator.clipboard.writeText(user.accountId);
                                    toast.success("Account ID copied");
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell>
                              {user.termsAndConditionsAccepted ? (
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  Accepted
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                  Pending
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {formatDate(user.lastSignInTime || user.firebaseLastSignIn || null)}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatDate(user.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      </div>

      {/* Account Details Dialog */}
      <Dialog open={accountDetailsOpen} onOpenChange={setAccountDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Account Details
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <span className="font-mono text-xs">{selectedAccountDetails?.account.accountId}</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => {
                  navigator.clipboard.writeText(selectedAccountDetails?.account.accountId || "");
                  toast.success("Account ID copied");
                }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingAccountDetails ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : selectedAccountDetails ? (
            <Tabs defaultValue="info" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-3 rounded-lg border border-border/70 bg-muted/30 p-1">
                <TabsTrigger value="info" className="gap-2">
                  <Building2 className="h-4 w-4" />
                  Account Info
                </TabsTrigger>
                <TabsTrigger value="activity" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  Activity
                </TabsTrigger>
                <TabsTrigger value="actions" className="gap-2">
                  <Mail className="h-4 w-4" />
                  Actions
                </TabsTrigger>
              </TabsList>
              
              {/* Account Info Tab */}
              <TabsContent value="info" className="mt-4 flex-1 space-y-5 overflow-y-auto">
                {/* Account Stats */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded-lg border border-border/70 bg-muted/30 p-4 text-center">
                    <p className="text-3xl font-semibold tabular-nums">{selectedAccountDetails.account.userCount ?? selectedAccountDetails.account.usersCount ?? 0}</p>
                    <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">Users</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/30 p-4 text-center">
                    <p className="text-3xl font-semibold tabular-nums">{selectedAccountDetails.account.transactionCount ?? selectedAccountDetails.account.transactionsCount ?? 0}</p>
                    <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">Transactions</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/30 p-4 text-center">
                    <p className="text-3xl font-semibold tabular-nums">{selectedAccountDetails.account.categoriesCount ?? 0}</p>
                    <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">Categories</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/30 p-4 text-center">
                    <p className="text-xl font-semibold tabular-nums">{formatDate(selectedAccountDetails.account.createdAt)}</p>
                    <p className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">Created</p>
                  </div>
                </div>

                {/* Features Status */}
                <div>
                  <h3 className="mb-2.5 text-lg font-semibold">Features</h3>
                  <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                    <div className="flex items-center justify-between rounded-lg border border-border/70 bg-muted/25 px-3 py-2.5">
                      <span className="text-sm font-medium">WhatsApp</span>
                      <Badge variant={selectedAccountDetails.account.whatsappEnabled ? "default" : "secondary"} className="text-xs">
                        {selectedAccountDetails.account.whatsappEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/70 bg-muted/25 px-3 py-2.5">
                      <span className="text-sm font-medium">Notifications</span>
                      <Badge variant={selectedAccountDetails.account.transactionNotificationsEnabled ? "default" : "secondary"} className="text-xs">
                        {selectedAccountDetails.account.transactionNotificationsEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/70 bg-muted/25 px-3 py-2.5">
                      <span className="text-sm font-medium">Google Sheets</span>
                      <Badge variant={selectedAccountDetails.account.googleSheetsEnabled ? "default" : "secondary"} className="text-xs">
                        {selectedAccountDetails.account.googleSheetsEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/70 bg-muted/25 px-3 py-2.5">
                      <span className="text-sm font-medium">Welcome Email</span>
                      <Badge variant={selectedAccountDetails.account.welcomeEmailSentAt ? "default" : "secondary"} className="text-xs">
                        {selectedAccountDetails.account.welcomeEmailSentAt ? "Sent" : "Not Sent"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border/70 bg-muted/25 px-3 py-2.5 md:col-span-2">
                      <span className="text-sm font-medium">Last Transaction</span>
                      <span className="text-sm tabular-nums font-semibold">{formatDate(selectedAccountDetails.account.lastTransactionDate)}</span>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Users Section */}
                <div>
                  <h3 className="mb-2.5 flex items-center gap-2 text-lg font-semibold">
                    <Users className="h-4 w-4" />
                    Users ({selectedAccountDetails.users.length})
                  </h3>
                  {selectedAccountDetails.users.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No users found</p>
                  ) : (
                    <div className="space-y-2.5">
                      {selectedAccountDetails.users.map((user) => (
                        <div
                          key={user.userId}
                          className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/25 p-3"
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-semibold">{user.fullName || "No name"}</p>
                            <div className="mt-0.5 flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1 truncate">
                                <Mail className="h-3 w-3" />
                                {user.email || "-"}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {user.phoneNumber || "-"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {user.whatsappEnabled && (
                              <Badge variant="outline" className="border-green-600 text-[11px] text-green-600">
                                <MessageSquare className="mr-1 h-3 w-3" />
                                WA
                              </Badge>
                            )}
                            {user.termsAndConditionsAccepted && (
                              <Badge variant="outline" className="text-[11px]">Terms ✓</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />
                
                {/* Categories Section */}
                <div>
                  <h3 className="mb-2.5 flex items-center gap-2 text-lg font-semibold">
                    <TrendingUp className="h-4 w-4" />
                    Categories ({selectedAccountDetails.categories.length})
                  </h3>
                  {selectedAccountDetails.categories.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No categories found</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {selectedAccountDetails.categories.map((category) => (
                        <div
                          key={category.id}
                          className="flex items-center gap-3 rounded-lg border border-border/70 bg-muted/25 p-3"
                        >
                          <div 
                            className="flex h-8 w-8 items-center justify-center rounded-full"
                            style={{ backgroundColor: category.color }}
                          >
                            <span className="text-white text-xs font-bold">
                              {category.type === 'income' ? '+' : '-'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm">{category.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {category.transactionsCount} {category.transactionsCount === 1 ? 'transaction' : 'transactions'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Activity Tab */}
              <TabsContent value="activity" className="flex-1 overflow-y-auto mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Latest Transactions
                  </h3>
                  <Badge variant="outline">
                    {selectedAccountDetails.transactions.length} transactions loaded
                  </Badge>
                </div>
                
                {selectedAccountDetails.transactions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <CreditCard className="h-12 w-12 mb-4 opacity-50" />
                    <p>No transactions found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedAccountDetails.transactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{tx.description || "No description"}</p>
                            {tx.source && (
                              <Badge variant="outline" className="text-xs">
                                {tx.source}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap mt-1">
                            <span className="flex items-center gap-1">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: tx.categoryColor || '#888' }}
                              />
                              {tx.categoryName || "Uncategorized"}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(tx.createdAt)}
                            </span>
                            {tx.createdByUserName && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {tx.createdByUserName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className={`font-semibold text-lg ${tx.amount < 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {tx.amount < 0 ? '-' : '+'}₪{Math.abs(tx.amount).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              {/* Actions Tab */}
              <TabsContent value="actions" className="flex-1 overflow-y-auto mt-4 space-y-6">
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Actions
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Welcome Email */}
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">Welcome Email</h4>
                          {selectedAccountDetails.account.welcomeEmailSentAt && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Sent
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Send a welcome email in Hebrew explaining system features and how to get started.
                        </p>
                        {selectedAccountDetails.account.welcomeEmailSentAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Sent: {format(new Date(selectedAccountDetails.account.welcomeEmailSentAt), "PPp")}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleSendWelcomeEmail(selectedAccountDetails.account.accountId)}
                        disabled={isSendingEmail}
                        variant={selectedAccountDetails.account.welcomeEmailSentAt ? "outline" : "default"}
                        className="gap-2 ml-4"
                      >
                        {isSendingEmail ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            {selectedAccountDetails.account.welcomeEmailSentAt ? "Resend" : "Send"}
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Activity Summary Email */}
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">Activity Summary Email</h4>
                          {selectedAccountDetails.account.activitySummaryEmailSentAt && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Sent
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Send a marketing email with usage tips, WhatsApp features, and encouragement to use the system.
                        </p>
                        {selectedAccountDetails.account.activitySummaryEmailSentAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Sent: {format(new Date(selectedAccountDetails.account.activitySummaryEmailSentAt), "PPp")}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleSendActivitySummaryEmail(selectedAccountDetails.account.accountId)}
                        disabled={isSendingEmail}
                        variant={selectedAccountDetails.account.activitySummaryEmailSentAt ? "outline" : "default"}
                        className="gap-2 ml-4"
                      >
                        {isSendingEmail ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <TrendingUp className="h-4 w-4" />
                            {selectedAccountDetails.account.activitySummaryEmailSentAt ? "Resend" : "Send Tips Email"}
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Engagement Email */}
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-orange-200">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">Engagement Email (Inactive Users)</h4>
                          {selectedAccountDetails.account.engagementEmailSentAt && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Sent
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Send to inactive users (30+ days) or incomplete onboarding. Warns about account deletion 1 month after creation.
                        </p>
                        {selectedAccountDetails.account.engagementEmailSentAt && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Sent: {format(new Date(selectedAccountDetails.account.engagementEmailSentAt), "PPp")}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleSendEngagementEmail(selectedAccountDetails.account.accountId)}
                        disabled={isSendingEmail}
                        variant={selectedAccountDetails.account.engagementEmailSentAt ? "outline" : "default"}
                        className="gap-2 ml-4"
                      >
                        {isSendingEmail ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-4 w-4" />
                            {selectedAccountDetails.account.engagementEmailSentAt ? "Resend" : "Send"}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Account Owner Info */}
                <div>
                  <h3 className="font-semibold mb-4">Account Owner</h3>
                  {selectedAccountDetails.users.find(u => u.role === 'owner') ? (
                    <div className="p-4 bg-muted/50 rounded-lg">
                      {(() => {
                        const owner = selectedAccountDetails.users.find(u => u.role === 'owner');
                        return owner ? (
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                              <User className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{owner.fullName || "No name set"}</p>
                              <p className="text-sm text-muted-foreground">{owner.email || "No email"}</p>
                              <p className="text-sm text-muted-foreground">{owner.phoneNumber || "No phone"}</p>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No owner found</p>
                  )}
                </div>
                
                <Separator />
                
                {/* Danger Zone */}
                <div>
                  <h3 className="font-semibold mb-4 text-destructive">Danger Zone</h3>
                  <div className="p-4 bg-destructive/5 rounded-lg border border-destructive/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-destructive">Delete Account</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Permanently delete this account and all its data.
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setAccountToDelete(selectedAccountDetails.account.accountId);
                          setAccountDetailsOpen(false);
                          setDeleteDialogOpen(true);
                        }}
                        className="gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteConfirmText("");
          setDeleteFirebaseUsers(false);
        }
        setDeleteDialogOpen(open);
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Account
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                This action <strong>cannot be undone</strong>. This will permanently delete the account
                and all associated data including users and transactions.
              </p>
              
              {/* Account ID Display */}
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Account ID to delete:</p>
                <p className="font-mono text-sm font-semibold break-all">{accountToDelete}</p>
              </div>
              
              {/* Firebase Users Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="deleteFirebase" 
                  checked={deleteFirebaseUsers}
                  onCheckedChange={(checked) => setDeleteFirebaseUsers(checked === true)}
                />
                <Label htmlFor="deleteFirebase" className="text-sm cursor-pointer">
                  Also delete Firebase authentication users
                </Label>
              </div>
              
              {/* Confirmation Input */}
              <div className="space-y-2">
                <Label htmlFor="confirmAccountId" className="text-sm">
                  Type the Account ID to confirm:
                </Label>
                <Input
                  id="confirmAccountId"
                  placeholder="Paste account ID here..."
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="font-mono text-sm"
                />
                {deleteConfirmText && deleteConfirmText !== accountToDelete && (
                  <p className="text-xs text-destructive">Account ID does not match</p>
                )}
                {deleteConfirmText && deleteConfirmText === accountToDelete && (
                  <p className="text-xs text-green-600">✓ Account ID confirmed</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== accountToDelete || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Account
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
